/**
 * Vue 3 Input Mask - Port of angular-ui/ui-mask
 *
 * Provides both a composable (useMask) and a Vue directive (vMask).
 *
 * Mask Definitions:
 *   '9' → digit
 *   'A' → letter
 *   '*' → alphanumeric
 *   '?' → marks subsequent characters as optional
 *   '\\' → escape next character as literal
 *
 * Usage as composable:
 *   const { maskedValue, unmaskedValue, inputRef, isValid } = useMask('(999) 999-9999', { ... })
 *
 * Usage as directive:
 *   <input v-mask="'(999) 999-9999'" />
 *   <input v-mask="{ mask: '99/99/9999', placeholder: 'DD/MM/YYYY' }" />
 */

import type { App, DirectiveBinding, Ref } from 'vue'
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface MaskDefinitions {
  [key: string]: RegExp
}

export interface MaskOptions {
  maskDefinitions?: MaskDefinitions
  clearOnBlur?: boolean
  clearOnBlurPlaceholder?: boolean
  escChar?: string
  placeholderChar?: string
  addDefaultPlaceholder?: boolean
  allowInvalidValue?: boolean
  placeholder?: string
  modelViewValue?: boolean
}

export interface MaskDirectiveValue extends MaskOptions {
  mask: string
}

interface MaskComponent {
  value: string
  position: number
}

interface MaskEngine {
  readonly maskProcessed: boolean
  readonly maskPlaceholder: string
  readonly maskCaretMap: number[]
  readonly minRequiredLength: number
  processRawMask: (mask: string) => void
  unmaskValue: (value: string, oldCaretPos?: number, selectionLen?: number) => string
  maskValue: (unmaskedValue: string) => string
  validateValue: (value: string) => boolean
  isValidCaretPosition: (pos: number) => boolean
  opts: Required<MaskOptions>
}

interface DirectiveState {
  engine: MaskEngine
  oldValue: string
  oldValueUnmasked: string
  oldCaretPosition: number
  oldSelectionLength: number
  preventBackspace: boolean
  prevValue: string
}

interface DirectiveHandlers {
  blur: () => void
  mouseDownUp: (e: MouseEvent) => void
  keydown: (e: KeyboardEvent) => void
  event: (e: Event) => void
}

interface MaskableHTMLInputElement extends HTMLInputElement {
  __maskState?: DirectiveState
  __maskHandlers?: DirectiveHandlers
}

export interface UseMaskReturn {
  inputRef: Ref<HTMLInputElement | null>
  maskedValue: Ref<string>
  unmaskedValue: Ref<string>
  isValid: Ref<boolean>
  setRawValue: (rawValue: string) => void
}

// ─── Default Config ────────────────────────────────────────────────────────────

const DEFAULT_CONFIG: Required<MaskOptions> = {
  maskDefinitions: {
    '9': /\d/,
    A: /[a-zA-Z]/,
    '*': /[a-zA-Z0-9]/,
  },
  clearOnBlur: true,
  clearOnBlurPlaceholder: false,
  escChar: '\\',
  placeholderChar: '_',
  addDefaultPlaceholder: true,
  allowInvalidValue: false,
  placeholder: undefined as unknown as string,
  modelViewValue: false,
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function isFocused(elem: HTMLElement): boolean {
  return (
    elem === document.activeElement &&
    (!document.hasFocus || document.hasFocus()) &&
    !!(elem.getAttribute('type') || (elem as HTMLAnchorElement).href || ~elem.tabIndex)
  )
}

function getCaretPosition(input: HTMLInputElement): number {
  if (!input) return 0
  if (input.selectionStart !== null && input.selectionStart !== undefined) {
    return input.selectionStart
  }
  return 0
}

function setCaretPosition(input: HTMLInputElement, pos: number): void {
  if (!input) return
  if (input.offsetWidth === 0 || input.offsetHeight === 0) return
  if (input.setSelectionRange && isFocused(input)) {
    input.focus()
    input.setSelectionRange(pos, pos)
  }
}

function getSelectionLength(input: HTMLInputElement): number {
  if (!input) return 0
  if (input.selectionStart !== null && input.selectionEnd !== null) {
    return input.selectionEnd - input.selectionStart
  }
  if (window.getSelection) {
    return window.getSelection()?.toString().length ?? 0
  }
  return 0
}

// ─── Core Mask Engine ──────────────────────────────────────────────────────────

function createMaskEngine(maskString: string, options: MaskOptions = {}): MaskEngine {
  const opts: Required<MaskOptions> = { ...DEFAULT_CONFIG, ...options }
  const maskCaretMap: number[] = []
  const maskPatterns: RegExp[] = []
  let maskPlaceholder = ''
  let minRequiredLength = 0
  let maskComponents: MaskComponent[] = []
  let maskProcessed = false

  function getPlaceholderChar(i: number): string {
    if (opts.placeholder && opts.placeholder[i]) {
      return opts.placeholder[i]
    }
    const ch = opts.placeholderChar || '_'
    return ch.toLowerCase() === 'space' ? ' ' : ch[0]
  }

  function processRawMask(mask: string): void {
    maskCaretMap.length = 0
    maskPatterns.length = 0
    maskPlaceholder = ''
    let characterCount = 0

    if (typeof mask === 'string' && mask.length > 0) {
      minRequiredLength = 0
      let isOptional = false
      let numberOfOptionalCharacters = 0
      let inEscape = false

      for (let i = 0; i < mask.length; i++) {
        const chr = mask[i]

        if (inEscape) {
          inEscape = false
          maskPlaceholder += chr
          characterCount++
        } else if (opts.escChar === chr) {
          inEscape = true
        } else if (opts.maskDefinitions[chr]) {
          maskCaretMap.push(characterCount)
          maskPlaceholder += getPlaceholderChar(i - numberOfOptionalCharacters)
          maskPatterns.push(opts.maskDefinitions[chr])
          characterCount++
          if (!isOptional) {
            minRequiredLength++
          }
          isOptional = false
        } else if (chr === '?') {
          isOptional = true
          numberOfOptionalCharacters++
        } else {
          maskPlaceholder += chr
          characterCount++
        }
      }
    }

    // Caret position immediately following last position is valid
    maskCaretMap.push((maskCaretMap.length > 0 ? maskCaretMap[maskCaretMap.length - 1] : -1) + 1)
    maskComponents = getMaskComponents()
    maskProcessed = maskCaretMap.length > 1
  }

  function getMaskComponents(): MaskComponent[] {
    const maskPlaceholderChars = maskPlaceholder.split('')

    if (maskCaretMap.length > 0 && !isNaN(maskCaretMap[0])) {
      for (const pos of maskCaretMap) {
        if (pos < maskPlaceholderChars.length) {
          maskPlaceholderChars[pos] = '_'
        }
      }
    }

    const maskPlaceholderCopy = maskPlaceholderChars.join('')
    let components = maskPlaceholderCopy.replace(/[_]+/g, '_').split('_')
    components = components.filter((s) => s !== '')

    let offset = 0
    return components.map((c) => {
      const componentPosition = maskPlaceholderCopy.indexOf(c, offset)
      offset = componentPosition + 1
      return { value: c, position: componentPosition }
    })
  }

  function unmaskValue(value: string, oldCaretPos: number = 0, selectionLen: number = 0): string {
    let valueUnmasked = ''
    const maskPatternsCopy = maskPatterns.slice()
    const selectionStart = oldCaretPos
    const selectionEnd = selectionStart + selectionLen

    let strValue = String(value)
    let valueOffset = 0
    const valueDelta = strValue.length - maskPlaceholder.length
    let tempValue = ''

    for (const component of maskComponents) {
      let position = component.position
      if (!(position >= selectionStart && position < selectionEnd)) {
        if (position >= selectionStart) {
          position += valueDelta
        }
        if (strValue.substring(position, position + component.value.length) === component.value) {
          tempValue += strValue.slice(valueOffset, position)
          valueOffset = position + component.value.length
        }
      }
    }

    strValue = tempValue + strValue.slice(valueOffset)

    for (const chr of strValue.split('')) {
      if (maskPatternsCopy.length && maskPatternsCopy[0].test(chr)) {
        valueUnmasked += chr
        maskPatternsCopy.shift()
      }
    }

    return valueUnmasked
  }

  function maskValue(unmaskedVal: string): string {
    let valueMasked = ''
    let remaining = unmaskedVal
    const maskCaretMapCopy = maskCaretMap.slice()
    const chars = maskPlaceholder.split('')

    for (let i = 0; i < chars.length; i++) {
      if (remaining.length && i === maskCaretMapCopy[0]) {
        valueMasked += remaining.charAt(0) || '_'
        remaining = remaining.substr(1)
        maskCaretMapCopy.shift()
      } else {
        valueMasked += chars[i]
      }
    }

    return valueMasked
  }

  function validateValue(value: string): boolean {
    return value.length ? value.length >= minRequiredLength : true
  }

  function isValidCaretPosition(pos: number): boolean {
    return maskCaretMap.indexOf(pos) > -1
  }

  // Initialize
  processRawMask(maskString)

  return {
    get maskProcessed() {
      return maskProcessed
    },
    get maskPlaceholder() {
      return maskPlaceholder
    },
    get maskCaretMap() {
      return maskCaretMap
    },
    get minRequiredLength() {
      return minRequiredLength
    },
    processRawMask,
    unmaskValue,
    maskValue,
    validateValue,
    isValidCaretPosition,
    opts,
  }
}

// ─── Composable: useMask ───────────────────────────────────────────────────────

export function useMask(mask: string | Ref<string>, options: MaskOptions = {}): UseMaskReturn {
  const inputRef = ref<HTMLInputElement | null>(null)
  const maskedValue = ref('')
  const unmaskedValue = ref('')
  const isValid = ref(true)

  const maskStr: Ref<string> = typeof mask === 'string' ? ref(mask) : mask
  let engine: MaskEngine | null = createMaskEngine(maskStr.value, options)
  let pendingRawValue: string | null = null

  // State for event handling
  let oldValue = ''
  let oldValueUnmasked = ''
  let oldCaretPosition = 0
  let oldSelectionLength = 0
  let preventBackspace = false
  let prevValue = ''
  let eventsBound = false

  function rebuildEngine(): void {
    engine = createMaskEngine(maskStr.value, options)
  }

  function applyMask(rawValue: string): string {
    if (!engine || !engine.maskProcessed) return rawValue || ''
    const unmasked = engine.unmaskValue(rawValue || '', 0, 0)
    const valid = engine.validateValue(unmasked)
    isValid.value = valid
    unmaskedValue.value = unmasked

    if (!unmasked.length) {
      maskedValue.value = ''
      return ''
    }

    if (valid || engine.opts.allowInvalidValue) {
      const masked = engine.maskValue(unmasked)
      maskedValue.value = masked
      return masked
    }

    maskedValue.value = ''
    return ''
  }

  /** Programmatically set the raw (unmasked) value */
  function setRawValue(rawValue: string): void {
    if (!engine || !engine.maskProcessed) return
    const masked = applyMask(rawValue)
    const input = inputRef.value
    if (input) {
      input.value = masked
      oldValue = masked
      oldValueUnmasked = unmaskedValue.value
    } else {
      // Input not mounted yet — defer DOM update
      pendingRawValue = rawValue
    }
  }

  // ── Event Handlers ───────────────────────────────────────────────────────

  function blurHandler(): void {
    if (!engine) return
    const input = inputRef.value
    if (!input) return

    if (
      engine.opts.clearOnBlur ||
      (engine.opts.clearOnBlurPlaceholder && unmaskedValue.value.length === 0)
    ) {
      oldCaretPosition = 0
      oldSelectionLength = 0
      if (!isValid.value || unmaskedValue.value.length === 0) {
        maskedValue.value = ''
        unmaskedValue.value = ''
        input.value = ''
      }
    }

    if (unmaskedValue.value !== prevValue) {
      input.dispatchEvent(new Event('change', { bubbles: true }))
    }
    prevValue = unmaskedValue.value
  }

  function keydownHandler(e: KeyboardEvent): void {
    if (!engine) return
    const input = e.target as HTMLInputElement
    const isKeyBackspace = e.which === 8
    const isCtrlZ = e.which === 90 && e.ctrlKey

    if (isKeyBackspace) {
      let caretPos = getCaretPosition(input) - 1 || 0
      while (caretPos >= 0) {
        if (engine.isValidCaretPosition(caretPos)) {
          setCaretPosition(input, caretPos + 1)
          break
        }
        caretPos--
      }
      preventBackspace = caretPos === -1
    }

    if (isCtrlZ) {
      input.value = ''
      e.preventDefault()
    }
  }

  function mouseDownUpHandler(e: MouseEvent): void {
    const input = e.target as HTMLInputElement
    if (e.type === 'mousedown') {
      input.addEventListener('mouseout', mouseoutHandler)
    } else {
      input.removeEventListener('mouseout', mouseoutHandler)
    }
  }

  function mouseoutHandler(e: Event): void {
    const input = e.target as HTMLInputElement
    oldSelectionLength = getSelectionLength(input)
    input.removeEventListener('mouseout', mouseoutHandler)
  }

  function eventHandler(e: Event): void {
    if (!engine || !engine.maskProcessed) return
    const input = e.target as HTMLInputElement

    const keyEvent = e as KeyboardEvent
    const eventWhich = keyEvent.which
    const eventType = e.type

    // Prevent shift and ctrl from mucking with old values
    if (eventWhich === 16 || eventWhich === 91) return

    const val = input.value
    const valOld = oldValue
    let valAltered = false
    let valUnmasked = engine.unmaskValue(val, oldCaretPosition, oldSelectionLength)
    const valUnmaskedOld = oldValueUnmasked
    let caretPos = getCaretPosition(input) || 0
    const caretPosOld = oldCaretPosition || 0
    const caretPosDelta = caretPos - caretPosOld
    const caretPosMin = engine.maskCaretMap[0]
    const caretPosMax =
      engine.maskCaretMap[valUnmasked.length] || engine.maskCaretMap.slice().shift()!
    const selectionLenOld = oldSelectionLength || 0
    const isSelected = getSelectionLength(input) > 0
    const wasSelected = selectionLenOld > 0

    const isAddition =
      val.length > valOld.length ||
      (selectionLenOld && val.length > valOld.length - selectionLenOld)
    const isDeletion =
      val.length < valOld.length ||
      (selectionLenOld && val.length === valOld.length - selectionLenOld)
    const isSelection = eventWhich >= 37 && eventWhich <= 40 && keyEvent.shiftKey
    const isKeyLeftArrow = eventWhich === 37
    const isKeyBackspace =
      eventWhich === 8 || (eventType !== 'keyup' && isDeletion && caretPosDelta === -1)
    const isKeyDelete =
      eventWhich === 46 ||
      (eventType !== 'keyup' && isDeletion && caretPosDelta === 0 && !wasSelected)
    const caretBumpBack =
      (isKeyLeftArrow || isKeyBackspace || eventType === 'click') && caretPos > caretPosMin

    oldSelectionLength = getSelectionLength(input)

    if (isSelection || (isSelected && (eventType === 'click' || eventType === 'keyup' || eventType === 'focus'))) {
      return
    }

    if (isKeyBackspace && preventBackspace) {
      input.value = engine.maskPlaceholder
      maskedValue.value = ''
      unmaskedValue.value = ''
      setCaretPosition(input, caretPosOld)
      return
    }

    // User attempted to delete but raw value was unaffected
    if (eventType === 'input' && isDeletion && !wasSelected && valUnmasked === valUnmaskedOld) {
      while (isKeyBackspace && caretPos > caretPosMin && !engine.isValidCaretPosition(caretPos)) {
        caretPos--
      }
      while (
        isKeyDelete &&
        caretPos < caretPosMax &&
        engine.maskCaretMap.indexOf(caretPos) === -1
      ) {
        caretPos++
      }
      const charIndex = engine.maskCaretMap.indexOf(caretPos)
      valUnmasked = valUnmasked.substring(0, charIndex) + valUnmasked.substring(charIndex + 1)
      if (valUnmasked !== valUnmaskedOld) valAltered = true
    }

    const valMasked = engine.maskValue(valUnmasked)
    oldValue = valMasked
    oldValueUnmasked = valUnmasked

    if (!valAltered && val.length > valMasked.length) valAltered = true

    input.value = valMasked
    maskedValue.value = valMasked
    unmaskedValue.value = valUnmasked
    isValid.value = engine.validateValue(valUnmasked)

    // Emit a custom event so v-model and parent components can react
    if (valAltered) {
      input.dispatchEvent(new Event('input', { bubbles: true }))
    }

    // Caret repositioning
    if (isAddition && caretPos <= caretPosMin) {
      caretPos = caretPosMin + 1
    }
    if (caretBumpBack) {
      caretPos--
    }
    caretPos = caretPos > caretPosMax ? caretPosMax : caretPos < caretPosMin ? caretPosMin : caretPos

    while (!engine.isValidCaretPosition(caretPos) && caretPos > caretPosMin && caretPos < caretPosMax) {
      caretPos += caretBumpBack ? -1 : 1
    }
    if ((caretBumpBack && caretPos < caretPosMax) || (isAddition && !engine.isValidCaretPosition(caretPosOld))) {
      caretPos++
    }

    oldCaretPosition = caretPos
    setCaretPosition(input, caretPos)
  }

  function bindEvents(): void {
    const input = inputRef.value
    if (!input || eventsBound) return

    input.addEventListener('blur', blurHandler)
    input.addEventListener('mousedown', mouseDownUpHandler)
    input.addEventListener('mouseup', mouseDownUpHandler)
    input.addEventListener('keydown', keydownHandler)
    input.addEventListener('input', eventHandler)
    input.addEventListener('keyup', eventHandler)
    input.addEventListener('click', eventHandler)
    input.addEventListener('focus', eventHandler)
    eventsBound = true
  }

  function unbindEvents(): void {
    const input = inputRef.value
    if (!input || !eventsBound) return

    input.removeEventListener('blur', blurHandler)
    input.removeEventListener('mousedown', mouseDownUpHandler)
    input.removeEventListener('mouseup', mouseDownUpHandler)
    input.removeEventListener('keydown', keydownHandler)
    input.removeEventListener('input', eventHandler)
    input.removeEventListener('keyup', eventHandler)
    input.removeEventListener('click', eventHandler)
    input.removeEventListener('focus', eventHandler)
    eventsBound = false
  }

  function initialize(): void {
    rebuildEngine()
    if (!engine || !engine.maskProcessed) return

    const input = inputRef.value
    if (!input) return

    if (engine.opts.addDefaultPlaceholder && !input.getAttribute('placeholder')) {
      input.setAttribute('placeholder', engine.maskPlaceholder)
    }

    // Apply pending value from pre-mount setRawValue, or initial input value
    const initialVal = pendingRawValue ?? input.value
    pendingRawValue = null
    if (initialVal) {
      const masked = applyMask(initialVal)
      input.value = masked
      oldValue = masked
      oldValueUnmasked = unmaskedValue.value
    } else {
      oldValue = ''
      oldValueUnmasked = ''
    }

    bindEvents()
  }

  // Watch mask changes
  watch(maskStr, () => {
    unbindEvents()
    initialize()
  })

  onMounted(() => {
    nextTick(initialize)
  })

  onBeforeUnmount(() => {
    unbindEvents()
  })

  return {
    inputRef,
    maskedValue,
    unmaskedValue,
    isValid,
    setRawValue,
  }
}

// ─── Directive: vMask ──────────────────────────────────────────────────────────

/**
 * Usage:
 *   v-mask="'(999) 999-9999'"
 *   v-mask="{ mask: '99/99/9999', placeholder: 'DD/MM/YYYY', clearOnBlur: false }"
 */
export const vMask = {
  mounted(el: HTMLElement, binding: DirectiveBinding<string | MaskDirectiveValue>): void {
    const input: MaskableHTMLInputElement | null =
      el.tagName === 'INPUT' ? (el as MaskableHTMLInputElement) : el.querySelector('input')
    if (!input) {
      console.warn('[v-mask] Directive requires an input element')
      return
    }

    const value = binding.value
    let maskStr: string
    let opts: MaskOptions
    if (typeof value === 'string') {
      maskStr = value
      opts = {}
    } else if (value && typeof value === 'object') {
      maskStr = value.mask
      const { mask: _mask, ...rest } = value
      opts = rest
    } else {
      return
    }

    const engine = createMaskEngine(maskStr, opts)
    if (!engine.maskProcessed) return

    // Store on element for cleanup and updates
    const state: DirectiveState = {
      engine,
      oldValue: '',
      oldValueUnmasked: '',
      oldCaretPosition: 0,
      oldSelectionLength: 0,
      preventBackspace: false,
      prevValue: '',
    }
    input.__maskState = state

    if (engine.opts.addDefaultPlaceholder && !input.getAttribute('placeholder')) {
      input.setAttribute('placeholder', engine.maskPlaceholder)
    }

    // Apply initial value
    if (input.value) {
      const unmasked = engine.unmaskValue(input.value, 0, 0)
      const masked = engine.maskValue(unmasked)
      input.value = masked
      state.oldValue = masked
      state.oldValueUnmasked = unmasked
    }

    // Bind handlers
    const handlers = createDirectiveHandlers(input, state)
    input.__maskHandlers = handlers

    input.addEventListener('blur', handlers.blur)
    input.addEventListener('mousedown', handlers.mouseDownUp as EventListener)
    input.addEventListener('mouseup', handlers.mouseDownUp as EventListener)
    input.addEventListener('keydown', handlers.keydown as EventListener)
    input.addEventListener('input', handlers.event)
    input.addEventListener('keyup', handlers.event)
    input.addEventListener('click', handlers.event)
    input.addEventListener('focus', handlers.event)
  },

  updated(el: HTMLElement, binding: DirectiveBinding<string | MaskDirectiveValue>): void {
    if (binding.value === binding.oldValue) return

    const input = el.tagName === 'INPUT' ? el : el.querySelector('input')
    if (!input) return

    // Clean up old handlers
    vMask.beforeUnmount(el)
    // Re-mount with new mask
    vMask.mounted(el, binding)
  },

  beforeUnmount(el: HTMLElement): void {
    const input: MaskableHTMLInputElement | null =
      el.tagName === 'INPUT' ? (el as MaskableHTMLInputElement) : el.querySelector('input')
    if (!input || !input.__maskHandlers) return

    const h = input.__maskHandlers
    input.removeEventListener('blur', h.blur)
    input.removeEventListener('mousedown', h.mouseDownUp as EventListener)
    input.removeEventListener('mouseup', h.mouseDownUp as EventListener)
    input.removeEventListener('keydown', h.keydown as EventListener)
    input.removeEventListener('input', h.event)
    input.removeEventListener('keyup', h.event)
    input.removeEventListener('click', h.event)
    input.removeEventListener('focus', h.event)

    delete input.__maskHandlers
    delete input.__maskState
  },
}

function createDirectiveHandlers(
  input: HTMLInputElement,
  state: DirectiveState
): DirectiveHandlers {
  const engine = state.engine

  function mouseoutHandler(e: Event): void {
    const target = e.target as HTMLInputElement
    state.oldSelectionLength = getSelectionLength(target)
    target.removeEventListener('mouseout', mouseoutHandler)
  }

  return {
    blur(): void {
      if (
        engine.opts.clearOnBlur ||
        (engine.opts.clearOnBlurPlaceholder && state.oldValueUnmasked.length === 0)
      ) {
        state.oldCaretPosition = 0
        state.oldSelectionLength = 0
        const currentUnmasked = engine.unmaskValue(input.value, 0, 0)
        const valid = engine.validateValue(currentUnmasked)
        if (!valid || currentUnmasked.length === 0) {
          input.value = ''
          input.dispatchEvent(new Event('input', { bubbles: true }))
        }
      }
      const currentUnmasked = engine.unmaskValue(input.value, 0, 0)
      if (currentUnmasked !== state.prevValue) {
        input.dispatchEvent(new Event('change', { bubbles: true }))
      }
      state.prevValue = currentUnmasked
    },

    mouseDownUp(e: MouseEvent): void {
      if (e.type === 'mousedown') {
        input.addEventListener('mouseout', mouseoutHandler)
      } else {
        input.removeEventListener('mouseout', mouseoutHandler)
      }
    },

    keydown(e: KeyboardEvent): void {
      const isKeyBackspace = e.which === 8
      const isCtrlZ = e.which === 90 && e.ctrlKey

      if (isKeyBackspace) {
        let caretPos = getCaretPosition(input) - 1 || 0
        while (caretPos >= 0) {
          if (engine.isValidCaretPosition(caretPos)) {
            setCaretPosition(input, caretPos + 1)
            break
          }
          caretPos--
        }
        state.preventBackspace = caretPos === -1
      }

      if (isCtrlZ) {
        input.value = ''
        e.preventDefault()
      }
    },

    event(e: Event): void {
      if (!engine.maskProcessed) return

      const keyEvent = e as KeyboardEvent
      const eventWhich = keyEvent.which
      const eventType = e.type

      if (eventWhich === 16 || eventWhich === 91) return

      const val = input.value
      const valOld = state.oldValue
      let valAltered = false
      let valUnmasked = engine.unmaskValue(val, state.oldCaretPosition, state.oldSelectionLength)
      const valUnmaskedOld = state.oldValueUnmasked
      let caretPos = getCaretPosition(input) || 0
      const caretPosOld = state.oldCaretPosition || 0
      const caretPosDelta = caretPos - caretPosOld
      const caretPosMin = engine.maskCaretMap[0]
      const caretPosMax =
        engine.maskCaretMap[valUnmasked.length] || engine.maskCaretMap.slice().shift()!
      const selectionLenOld = state.oldSelectionLength || 0
      const isSelected = getSelectionLength(input) > 0
      const wasSelected = selectionLenOld > 0

      const isAddition =
        val.length > valOld.length ||
        (selectionLenOld && val.length > valOld.length - selectionLenOld)
      const isDeletion =
        val.length < valOld.length ||
        (selectionLenOld && val.length === valOld.length - selectionLenOld)
      const isSelection = eventWhich >= 37 && eventWhich <= 40 && keyEvent.shiftKey
      const isKeyLeftArrow = eventWhich === 37
      const isKeyBackspace =
        eventWhich === 8 || (eventType !== 'keyup' && isDeletion && caretPosDelta === -1)
      const isKeyDelete =
        eventWhich === 46 ||
        (eventType !== 'keyup' && isDeletion && caretPosDelta === 0 && !wasSelected)
      const caretBumpBack =
        (isKeyLeftArrow || isKeyBackspace || eventType === 'click') && caretPos > caretPosMin

      state.oldSelectionLength = getSelectionLength(input)

      if (isSelection || (isSelected && (eventType === 'click' || eventType === 'keyup' || eventType === 'focus'))) {
        return
      }

      if (isKeyBackspace && state.preventBackspace) {
        input.value = engine.maskPlaceholder
        setCaretPosition(input, caretPosOld)
        return
      }

      if (eventType === 'input' && isDeletion && !wasSelected && valUnmasked === valUnmaskedOld) {
        while (isKeyBackspace && caretPos > caretPosMin && !engine.isValidCaretPosition(caretPos)) {
          caretPos--
        }
        while (isKeyDelete && caretPos < caretPosMax && engine.maskCaretMap.indexOf(caretPos) === -1) {
          caretPos++
        }
        const charIndex = engine.maskCaretMap.indexOf(caretPos)
        valUnmasked = valUnmasked.substring(0, charIndex) + valUnmasked.substring(charIndex + 1)
        if (valUnmasked !== valUnmaskedOld) valAltered = true
      }

      const valMasked = engine.maskValue(valUnmasked)
      state.oldValue = valMasked
      state.oldValueUnmasked = valUnmasked

      if (!valAltered && val.length > valMasked.length) valAltered = true

      input.value = valMasked

      if (valAltered) {
        input.dispatchEvent(new Event('input', { bubbles: true }))
      }

      if (isAddition && caretPos <= caretPosMin) {
        caretPos = caretPosMin + 1
      }
      if (caretBumpBack) {
        caretPos--
      }
      caretPos =
        caretPos > caretPosMax ? caretPosMax : caretPos < caretPosMin ? caretPosMin : caretPos

      while (!engine.isValidCaretPosition(caretPos) && caretPos > caretPosMin && caretPos < caretPosMax) {
        caretPos += caretBumpBack ? -1 : 1
      }
      if ((caretBumpBack && caretPos < caretPosMax) || (isAddition && !engine.isValidCaretPosition(caretPosOld))) {
        caretPos++
      }

      state.oldCaretPosition = caretPos
      setCaretPosition(input, caretPos)
    },
  }
}

// ─── Plugin Install ────────────────────────────────────────────────────────────

export default {
  install(app: App): void {
    app.directive('mask', vMask)
  },
}
